import { Box, Button, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { borderRadius, elevation, spacing } from "../../theme";
import { ReviewStatus } from "../../types/duplicateAudit";

export interface AuditFilterValues {
  from: Dayjs | null;
  to: Dayjs | null;
  createdBy: string;
  minScore: number;
  reviewStatus: ReviewStatus;
  search: string;
}

export const DEFAULT_DAYS_BACK = 30;

export const getDefaultFilters = (): AuditFilterValues => ({
  from: dayjs().subtract(DEFAULT_DAYS_BACK, "day").startOf("day"),
  to: dayjs().startOf("day"),
  createdBy: "",
  minScore: 0,
  reviewStatus: "All",
  search: "",
});

// Partially typed dates are invalid; treat them as unset so no bad query is sent.
const toDayjs = (value: unknown): Dayjs | null => {
  const parsed = value ? (value as Dayjs) : null;
  return parsed && parsed.isValid() ? parsed : null;
};

const MIN_SCORE_OPTIONS = [
  { value: 0, label: "All scores" },
  { value: 70, label: "70%+" },
  { value: 80, label: "80%+" },
  { value: 90, label: "90%+" },
  { value: 95, label: "95%+" },
];

interface Props {
  values: AuditFilterValues;
  onChange: (values: AuditFilterValues) => void;
  onClear: () => void;
}

export default function AuditFilters({ values, onChange, onClear }: Props) {
  const set = <K extends keyof AuditFilterValues>(key: K, value: AuditFilterValues[K]) =>
    onChange({ ...values, [key]: value });

  const rangeInvalid =
    !!values.from && !!values.to && values.from.isAfter(values.to, "day");

  return (
    <Paper
      elevation={elevation.low}
      sx={{ p: spacing.mui.md, borderRadius: borderRadius.mui.medium }}
    >
      <Typography variant="subtitle1" fontWeight={700} mb={spacing.mui.sm}>
        Filters
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
              xl: "170px 170px 220px 160px 160px minmax(200px, 1fr) auto",
            },
            gap: spacing.mui.sm,
            alignItems: "start",
          }}
        >
          <DatePicker
            label="From"
            value={values.from}
            onChange={(v) => set("from", toDayjs(v))}
            format="DD/MM/YYYY"
            sx={{ width: "100%" }}
            slotProps={{
              textField: { size: "small", error: rangeInvalid },
            }}
          />
          <DatePicker
            label="To"
            value={values.to}
            onChange={(v) => set("to", toDayjs(v))}
            format="DD/MM/YYYY"
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                size: "small",
                error: rangeInvalid,
                helperText: rangeInvalid ? "'To' must not be before 'From'" : undefined,
              },
            }}
          />
          <TextField
            label="Created by"
            size="small"
            value={values.createdBy}
            onChange={(e) => set("createdBy", e.target.value)}
            autoComplete="off"
            fullWidth
          />
          <TextField
            select
            label="Minimum score"
            size="small"
            value={values.minScore}
            onChange={(e) => set("minScore", Number(e.target.value))}
            fullWidth
          >
            {MIN_SCORE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Review status"
            size="small"
            value={values.reviewStatus}
            onChange={(e) => set("reviewStatus", e.target.value as ReviewStatus)}
            fullWidth
          >
            <MenuItem value="All">All statuses</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Reviewed">Reviewed</MenuItem>
          </TextField>
          <TextField
            label="Customer search"
            size="small"
            value={values.search}
            onChange={(e) => set("search", e.target.value)}
            placeholder="Name, email or ID"
            autoComplete="off"
            fullWidth
            sx={{ gridColumn: { lg: "span 2", xl: "span 1" } }}
          />
          <Button variant="outlined" size="small" onClick={onClear} sx={{ height: 40, justifySelf: "end", gridColumn: { xs: "1 / -1", lg: "auto" } }}>
            Clear filters
          </Button>
        </Box>
      </LocalizationProvider>
    </Paper>
  );
}
